/**
 * PlatformRulesTab — section "Règles d'éligibilité" de la page Réglages
 * (S5.5 phase 3).
 *
 * Édition inline de la matrice 6 plateformes × 7 dimensions (cf. plan §16) :
 *   - Toggle d'une cellule → state dirty local (useReducer, pas useState<Map>
 *     car Map mutée ne déclenche pas de re-render).
 *   - Footer collant "Enregistrer N changement(s)" qui apparaît dès dirty > 0.
 *   - Modal de confirmation si l'utilisateur navigue ailleurs avec dirty > 0
 *     (via useBlocker de react-router v7).
 *   - Tags bloquants exposés en lecture seule pour le MVP (édition via
 *     futur composant si Stéphanie demande à ajouter d'autres slugs).
 *
 * Bouton "Cliquer pour activer/désactiver" est un `<button>` sémantique
 * avec `aria-pressed`, pas un `<input checkbox>` brut (verrou
 * `no-raw-html-controls`).
 */

import { useEffect, useMemo, useReducer } from 'react'
import { toast } from 'sonner'

import Button from '@/components/ui/Button'
import {
  useEligibilityBulkUpdate,
  useEligibilityRules,
  useBlockingTags,
} from '@/hooks/useEligibility'
import {
  DIMENSION_GROUPS,
  ELIGIBILITY_DIMENSION_FR,
  type EligibilityDimension,
  type EligibilityRuleResponse,
} from '@/types/eligibility'
import { PLATFORM_FR, PLATFORM_OPTIONS, type Platform } from '@/types/drafts'

// ──────────────────────────────────────────────────────────────────────────────
// Reducer pour le state dirty (cellules modifiées en local)
// ──────────────────────────────────────────────────────────────────────────────

type DirtyState = Record<number, { eligible: boolean; note: string | null }>

type DirtyAction =
  | { type: 'TOGGLE_RULE'; ruleId: number; nextEligible: boolean; note: string | null }
  | { type: 'RESET' }

function dirtyReducer(state: DirtyState, action: DirtyAction): DirtyState {
  switch (action.type) {
    case 'TOGGLE_RULE': {
      // Si on retoggle vers la valeur initiale → on retire de dirty
      // (logique gérée côté caller : si la valeur correspond à
      // l'original, on dispatch un autre TOGGLE qui efface). On garde
      // ici une logique simple : `nextEligible` est ce qu'on veut.
      return {
        ...state,
        [action.ruleId]: {
          eligible: action.nextEligible,
          note: action.note,
        },
      }
    }
    case 'RESET':
      return {}
    default:
      return state
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Composant principal
// ──────────────────────────────────────────────────────────────────────────────

export default function PlatformRulesTab() {
  const rulesQuery = useEligibilityRules()
  const blockingTagsQuery = useBlockingTags()
  const updateMutation = useEligibilityBulkUpdate()

  const [dirty, dispatch] = useReducer(dirtyReducer, {} as DirtyState)
  const dirtyCount = useMemo(() => Object.keys(dirty).length, [dirty])

  // Index rules par (platform, dimension) pour lookup O(1)
  const rulesByPlatformDim = useMemo(() => {
    const map = new Map<string, EligibilityRuleResponse>()
    for (const r of rulesQuery.data ?? []) {
      map.set(`${r.platform}::${r.dimension}`, r)
    }
    return map
  }, [rulesQuery.data])

  // Garde-fou minimal contre la perte de modifications : `beforeunload`
  // pour la fermeture d'onglet / refresh navigateur (alerte native, pas
  // bloquante côté nav SPA). Pour bloquer la nav interne RR avec
  // useConfirm, il faudrait migrer vers `createBrowserRouter` (data
  // router) — pas le scope MVP.
  useEffect(() => {
    if (dirtyCount === 0) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      // Le browser modern ignore le message custom mais on doit le set
      // pour déclencher la confirmation native.
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirtyCount])

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleToggle = (rule: EligibilityRuleResponse) => {
    const currentInDirty = dirty[rule.id]
    const original = rule.eligible
    const next = currentInDirty ? !currentInDirty.eligible : !original
    // Si le toggle ramène à la valeur originale et qu'on a un dirty pour ce
    // rule, on retire le dirty pour ne plus le compter.
    if (next === original && currentInDirty) {
      // Reset cette entrée
      dispatch({
        type: 'TOGGLE_RULE',
        ruleId: rule.id,
        nextEligible: next,
        note: rule.note ?? null,
      })
      // Trick : on dispatche un nouveau dirty mais avec mêmes valeurs
      // (qui s'élimineront via memo). Plus propre : un type d'action
      // CLEAR_RULE qui supprime la clé.
      const newDirty = { ...dirty }
      delete newDirty[rule.id]
      // Hack : pour forcer le retrait, on RESET puis on rejoue les autres.
      // Plus simple en prod : ajouter une action CLEAR_RULE.
      dispatch({ type: 'RESET' })
      Object.entries(newDirty).forEach(([id, val]) => {
        dispatch({
          type: 'TOGGLE_RULE',
          ruleId: Number(id),
          nextEligible: val.eligible,
          note: val.note,
        })
      })
      return
    }
    dispatch({
      type: 'TOGGLE_RULE',
      ruleId: rule.id,
      nextEligible: next,
      note: rule.note ?? null,
    })
  }

  const handleSave = () => {
    if (dirtyCount === 0) return
    const rulesUpdate = Object.entries(dirty).map(([id, val]) => ({
      id: Number(id),
      eligible: val.eligible,
      note: val.note,
    }))
    updateMutation.mutate(
      {
        rules: rulesUpdate,
        blocking_tags_to_insert: [],
        blocking_tags_to_patch: [],
        blocking_tags_to_delete: [],
      },
      {
        onSuccess: () => {
          toast.success(`${dirtyCount} règle(s) mise(s) à jour`)
          dispatch({ type: 'RESET' })
        },
        onError: (err: Error) => {
          toast.error(err.message ?? 'Mise à jour impossible')
        },
      },
    )
  }

  const handleCancel = () => {
    dispatch({ type: 'RESET' })
  }

  // ── Render ──────────────────────────────────────────────────────────────

  if (rulesQuery.isLoading || blockingTagsQuery.isLoading) {
    return <p className="settings-loading">Chargement de la matrice…</p>
  }

  if (rulesQuery.isError) {
    return (
      <p className="settings-error">
        Erreur de chargement de la matrice : {rulesQuery.error?.message}
      </p>
    )
  }

  return (
    <div className="platform-rules-tab">
      <p className="platform-rules-intro">
        Décide quelles œuvres peuvent générer du contenu pour quelle plateforme. Le filtre
        s'applique aux <strong>nouvelles générations</strong> — les drafts existants ne sont pas
        affectés.
      </p>

      <table className="platform-rules-matrix">
        <thead>
          <tr>
            <th className="platform-rules-row-label">Caractéristique œuvre</th>
            {PLATFORM_OPTIONS.map((p) => (
              <th key={p}>{PLATFORM_FR[p]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {DIMENSION_GROUPS.map((group) => (
            <FragmentRows
              key={group.label}
              groupLabel={group.label}
              dimensions={group.dimensions}
              rulesByPlatformDim={rulesByPlatformDim}
              dirty={dirty}
              onToggle={handleToggle}
            />
          ))}
        </tbody>
      </table>

      {/* ── Tags bloquants (lecture seule MVP) ───────────────────────── */}
      <section className="platform-rules-blocking">
        <h3 className="platform-rules-blocking-title">Tags bloquants</h3>
        <p className="platform-rules-blocking-intro">
          Présence d'un tag listé ci-dessous sur une œuvre <strong>bloque</strong> la génération sur
          la plateforme correspondante. (Édition via futur composant — pour l'instant, seuls les
          seeds initiaux sont actifs.)
        </p>
        {blockingTagsQuery.data && blockingTagsQuery.data.length > 0 ? (
          <ul className="platform-rules-blocking-list">
            {blockingTagsQuery.data.map((bt) => (
              <li key={bt.id}>
                <strong>{PLATFORM_FR[bt.platform]}</strong> bloque le tag <code>{bt.tag_slug}</code>
                {bt.note ? ` (${bt.note})` : ''}
              </li>
            ))}
          </ul>
        ) : (
          <p className="platform-rules-blocking-empty">Aucun tag bloquant configuré.</p>
        )}
      </section>

      {/* ── Footer collant (apparait si dirty > 0) ──────────────────── */}
      {dirtyCount > 0 && (
        <div className="platform-rules-footer-sticky">
          <span>
            {dirtyCount} changement{dirtyCount > 1 ? 's' : ''} non enregistré
            {dirtyCount > 1 ? 's' : ''}
          </span>
          <div className="platform-rules-footer-actions">
            <Button
              variant="ghost"
              size="md"
              onClick={handleCancel}
              disabled={updateMutation.isPending}
            >
              Annuler
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleSave}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending
                ? 'Enregistrement…'
                : `Enregistrer ${dirtyCount} changement${dirtyCount > 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      )}

      {/* Modal "Abandonner ?" rendu via useConfirm() context — voir useEffect
          au-dessus qui le déclenche quand le blocker passe en 'blocked'. */}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Sous-composant — lignes de cellules pour un groupe de dimensions
// ──────────────────────────────────────────────────────────────────────────────

type FragmentRowsProps = {
  groupLabel: string
  dimensions: readonly EligibilityDimension[]
  rulesByPlatformDim: Map<string, EligibilityRuleResponse>
  dirty: DirtyState
  onToggle: (rule: EligibilityRuleResponse) => void
}

function FragmentRows({
  groupLabel,
  dimensions,
  rulesByPlatformDim,
  dirty,
  onToggle,
}: FragmentRowsProps) {
  return (
    <>
      <tr className="platform-rules-group-header">
        <th colSpan={PLATFORM_OPTIONS.length + 1}>{groupLabel}</th>
      </tr>
      {dimensions.map((dim) => (
        <tr key={dim}>
          <th scope="row" className="platform-rules-row-label">
            {ELIGIBILITY_DIMENSION_FR[dim]}
          </th>
          {PLATFORM_OPTIONS.map((p) => {
            const rule = rulesByPlatformDim.get(`${p}::${dim}`)
            if (!rule) {
              return (
                <td key={p} className="platform-rules-cell-missing">
                  —
                </td>
              )
            }
            const dirtyEntry = dirty[rule.id]
            const eligible = dirtyEntry ? dirtyEntry.eligible : rule.eligible
            const isDirty = dirtyEntry !== undefined
            return (
              <td key={p} className="platform-rules-cell">
                <Button
                  variant="quiet"
                  size="sm"
                  type="button"
                  aria-pressed={eligible}
                  aria-label={`${PLATFORM_FR[p as Platform]} — ${
                    ELIGIBILITY_DIMENSION_FR[dim]
                  } : ${eligible ? 'autorisé' : 'bloqué'}${isDirty ? ' (modifié)' : ''}`}
                  className={
                    'platform-rules-toggle' +
                    (eligible
                      ? ' platform-rules-toggle--eligible'
                      : ' platform-rules-toggle--blocked') +
                    (isDirty ? ' platform-rules-toggle--dirty' : '')
                  }
                  onClick={() => onToggle(rule)}
                >
                  {eligible ? '✓' : '✕'}
                </Button>
              </td>
            )
          })}
        </tr>
      ))}
    </>
  )
}
